import { createSlice, createAsyncThunk, } from '@reduxjs/toolkit';
import { getApiClient } from '../../utils/apiClient';
// Async thunks
const getBulkInfo = createAsyncThunk('bulk/getBulkInfo', async (id, { rejectWithValue }) => {
    try {
        const response = await getApiClient()({
            url: '/api/bulk-info',
            method: 'get',
            data: { id },
        });
        return response.data;
    }
    catch (error) {
        const apiError = error;
        return rejectWithValue(apiError.message);
    }
});
const bulkOnboard = createAsyncThunk('bulk/bulkOnboard', async ({ id, code, onSuccess, onError, }, { rejectWithValue }) => {
    try {
        const response = await getApiClient()({
            url: '/api/bulk-onboard',
            method: 'post',
            data: { id, code },
        });
        onSuccess?.();
        return response.data;
    }
    catch (error) {
        const apiError = error;
        onError?.(apiError.message);
        return rejectWithValue(apiError.message);
    }
});
// Initial state
const initialState = {
    purchasedAmount: 0,
    onboardedCount: 0,
    isUserOnboarded: false,
    durationDays: 0,
    errorMsg: '',
    loading: false,
    onboardLoading: false,
};
// Bulk slice
const bulkSlice = createSlice({
    name: 'bulk',
    initialState,
    reducers: {
        // Clear error message
        clearError: (state) => {
            state.errorMsg = '';
        },
        // Set bulk info directly
        setBulkInfo: (state, action) => {
            state.purchasedAmount = action.payload.purchasedAmount;
            state.onboardedCount = action.payload.onboardedCount;
            state.isUserOnboarded = action.payload.isUserOnboarded;
            state.durationDays = action.payload.durationDays;
        },
        // Set user onboarded status
        setUserOnboarded: (state, action) => {
            state.isUserOnboarded = action.payload.isUserOnboarded;
        },
        // Update onboarded count
        updateOnboardedCount: (state, action) => {
            state.onboardedCount = action.payload.onboardedCount;
        },
        // Set error message
        setErrorMsg: (state, action) => {
            state.errorMsg = action.payload.errorMsg;
        },
        // Reset bulk state
        resetBulkState: () => initialState,
    },
    extraReducers: (builder) => {
        // Get bulk info
        builder
            .addCase(getBulkInfo.pending, (state) => {
            state.loading = true;
            state.errorMsg = '';
        })
            .addCase(getBulkInfo.fulfilled, (state, action) => {
            state.loading = false;
            state.purchasedAmount = action.payload.purchasedAmount;
            state.onboardedCount = action.payload.onboardedCount;
            state.isUserOnboarded = action.payload.isUserOnboarded;
            state.durationDays = action.payload.durationDays;
            state.errorMsg = '';
        })
            .addCase(getBulkInfo.rejected, (state, action) => {
            state.loading = false;
            state.errorMsg = action.payload;
        });
        // Bulk onboard
        builder
            .addCase(bulkOnboard.pending, (state) => {
            state.onboardLoading = true;
            state.errorMsg = '';
        })
            .addCase(bulkOnboard.fulfilled, (state) => {
            state.onboardLoading = false;
            state.isUserOnboarded = true;
            state.errorMsg = '';
            // Increment onboarded count when user successfully onboards
            state.onboardedCount += 1;
        })
            .addCase(bulkOnboard.rejected, (state, action) => {
            state.onboardLoading = false;
            state.errorMsg = action.payload;
        });
    },
});
// Export individual actions
const { clearError, setBulkInfo, setUserOnboarded, updateOnboardedCount, setErrorMsg, resetBulkState, } = bulkSlice.actions;
// Selectors
const selectBulkState = (state) => state.bulk;
const selectPurchasedAmount = (state) => state.bulk.purchasedAmount;
const selectOnboardedCount = (state) => state.bulk.onboardedCount;
const selectIsUserOnboarded = (state) => state.bulk.isUserOnboarded;
const selectDurationDays = (state) => state.bulk.durationDays;
const selectBulkErrorMsg = (state) => state.bulk.errorMsg;
const selectBulkLoading = (state) => state.bulk.loading;
const selectOnboardLoading = (state) => state.bulk.onboardLoading;
const selectIsBulkLoading = (state) => state.bulk.loading || state.bulk.onboardLoading;
// Computed selectors
const selectAvailableSlots = (state) => state.bulk.purchasedAmount - state.bulk.onboardedCount;
const selectIsOnboardingAvailable = (state) => !state.bulk.isUserOnboarded &&
    state.bulk.purchasedAmount > state.bulk.onboardedCount;
const selectOnboardingProgress = (state) => {
    if (state.bulk.purchasedAmount === 0)
        return 0;
    return ((state.bulk.onboardedCount / state.bulk.purchasedAmount) * 100);
};
// Grouped exports for better organization
export const bulkActions = {
    ...bulkSlice.actions,
    // Sync actions
    clearError,
    setBulkInfo,
    setUserOnboarded,
    updateOnboardedCount,
    setErrorMsg,
    resetBulkState,
    // Async actions
    getBulkInfo,
    bulkOnboard,
};
export const bulkSelectors = {
    selectBulkState,
    selectPurchasedAmount,
    selectOnboardedCount,
    selectIsUserOnboarded,
    selectDurationDays,
    selectBulkErrorMsg,
    selectBulkLoading,
    selectOnboardLoading,
    selectIsBulkLoading,
    selectAvailableSlots,
    selectIsOnboardingAvailable,
    selectOnboardingProgress,
};
// Export reducer
export default bulkSlice.reducer;
